import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { useT } from '@/utils/i18n';

export interface InteractiveLoginPayload {
  status?: string;
  operation_id: string;
  login_id: string;
  login_url: string;
  start_url?: string;
  expires_at?: number;
  tool_call_id?: string;
  cookie_path?: string;
  cookie_count?: number;
  domains?: string[];
}

export interface InteractiveLoginCompleteResult {
  status?: string;
  cookie_path?: string;
  cookie_count?: number;
  domains?: string[];
}

export function tryParseInteractiveLoginPayload(
  jsonText: string
): InteractiveLoginPayload | null {
  try {
    const parsed = JSON.parse(jsonText);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { login_id?: unknown }).login_id === 'string' &&
      typeof (parsed as { login_url?: unknown }).login_url === 'string'
    ) {
      return parsed as InteractiveLoginPayload;
    }
  } catch {
    // not valid JSON
  }
  return null;
}

function loginExpired(payload: InteractiveLoginPayload): boolean {
  return (
    typeof payload.expires_at === 'number' &&
    Date.now() / 1000 > payload.expires_at
  );
}

export type InteractiveLoginCompleter = (
  loginId: string
) => Promise<InteractiveLoginCompleteResult>;

const InteractiveLoginCompleterContext =
  createContext<InteractiveLoginCompleter | null>(null);

export function InteractiveLoginCompleterProvider({
  complete,
  children,
}: {
  complete: InteractiveLoginCompleter;
  children: ReactNode;
}) {
  return (
    <InteractiveLoginCompleterContext.Provider value={complete}>
      {children}
    </InteractiveLoginCompleterContext.Provider>
  );
}

function defaultCompleterViaBridge(): InteractiveLoginCompleter {
  return async loginId => {
    const bridge = window.jsb?.MessagesBridge;
    if (!bridge?.completeInteractiveLogin) {
      throw new Error('Interactive login completion is not available');
    }
    const raw = await bridge.completeInteractiveLogin(loginId);
    return {
      status: raw.status,
      cookie_path: raw.cookiePath,
      cookie_count: raw.cookieCount,
      domains: raw.domains,
    };
  };
}

export interface InteractiveLoginCardProps {
  payload: InteractiveLoginPayload;
  /** Opens the login URL externally. Defaults to window.open. */
  onOpen?: (loginUrl: string) => void;
}

export function InteractiveLoginCard({
  payload,
  onOpen,
}: InteractiveLoginCardProps) {
  const t = useT();
  const contextCompleter = useContext(InteractiveLoginCompleterContext);
  const completer = useMemo<InteractiveLoginCompleter>(
    () => contextCompleter ?? defaultCompleterViaBridge(),
    [contextCompleter]
  );
  const [completing, setCompleting] = useState(false);
  const [completedPath, setCompletedPath] = useState<string | null>(
    payload.status === 'completed' ? (payload.cookie_path ?? '') : null
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const expired = useMemo(() => loginExpired(payload), [payload]);

  const handleOpen = () => {
    if (onOpen) {
      onOpen(payload.login_url);
      return;
    }
    const utilsBridge = window.jsb?.UtilsBridge;
    if (utilsBridge?.openURL) {
      utilsBridge.openURL(payload.login_url).catch(() => {
        window.open(payload.login_url, '_blank', 'noopener,noreferrer');
      });
      return;
    }
    window.open(payload.login_url, '_blank', 'noopener,noreferrer');
  };

  const handleComplete = async () => {
    if (completing || completedPath !== null) {
      return;
    }
    setCompleting(true);
    setErrorMessage(null);
    try {
      const result = await completer(payload.login_id);
      setCompletedPath(result.cookie_path ?? '');
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setErrorMessage(
        message === 'Interactive login completion is not available'
          ? t('Interactive login completion is not available')
          : message
      );
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-2xl',
        'border border-border bg-surface-card'
      )}
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <div className="h-2 w-2 rounded-full bg-amber-400" />
        <span className="text-xs font-medium text-text-primary">
          {t('Interactive login')}
        </span>
      </div>

      <div className="flex flex-col gap-2 p-3">
        {payload.start_url ? (
          <div className="text-xs text-text-secondary break-all">
            {payload.start_url}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleOpen}
            disabled={expired}
            className={cn(
              'rounded-md border border-border bg-surface-card px-3 py-1 text-xs',
              'hover:bg-surface-elevated disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {t('Open login')}
          </button>
          <button
            type="button"
            onClick={handleComplete}
            disabled={completing || completedPath !== null}
            className={cn(
              'rounded-md border border-border bg-surface-card px-3 py-1 text-xs',
              'hover:bg-surface-elevated disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {completedPath !== null
              ? t('Completed')
              : completing
                ? t('Completing…')
                : t('Complete login')}
          </button>
        </div>

        {expired && completedPath === null ? (
          <div className="text-xs text-amber-400">
            {t('Login link expired.')}
          </div>
        ) : null}

        {completedPath ? (
          <div className="text-xs text-text-secondary break-all">
            {t('Saved to {path}', { path: completedPath })}
          </div>
        ) : null}

        {errorMessage ? (
          <div className="text-xs text-error-fg">{errorMessage}</div>
        ) : null}
      </div>
    </div>
  );
}
